import React, { useEffect, useState } from "react";
import { Modal, Form, Input, Row, Col, Select, Checkbox, Divider, message } from "antd";
import firebase from "firebase/compat/app";
import { newOpId, stamp } from "../../../modules/helpers/audit";

export default function ReturnAddressModal({ companyId, messageId, record, onClose }) {
  const db = firebase.firestore();
  const [form] = Form.useForm();
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [saveToBook, setSaveToBook] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Charge le carnet du client + pré-remplit avec l'adresse actuelle du destinataire
  useEffect(() => {
    if (!record || !companyId) return;

    setSaveToBook(false);
    form.setFieldsValue({
      return_address_name: record.return_address_name || "",
      return_address_line_1: record.return_address_line_1 || "",
      return_address_line_2: record.return_address_line_2 || "",
      return_address_postal_code: record.return_address_postal_code || "",
      return_address_city: record.return_address_city || "",
      book_label: "",
    });

    db.collection("companies").doc(companyId)
      .collection("return_addresses")
      .orderBy("is_default", "desc")
      .get()
      .then((snap) => setSavedAddresses(snap.docs.map((d) => ({ id: d.id, ...d.data() }))))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record, companyId]);

  function applySaved(addressId) {
    const addr = savedAddresses.find((a) => a.id === addressId);
    if (!addr) return;
    form.setFieldsValue({
      return_address_name: addr.name || "",
      return_address_line_1: addr.address_line_1 || "",
      return_address_line_2: addr.address_line_2 || "",
      return_address_postal_code: addr.postal_code || "",
      return_address_city: addr.city || "",
    });
  }

  async function handleSave() {
    if (!companyId || !messageId || !record) return;
    let values;
    try {
      values = await form.validateFields();
    } catch (e) {
      return; // erreurs de validation affichées par le form
    }

    setSubmitting(true);
    // Même opération pour l'adresse appliquée et son ajout éventuel au carnet
    const op = newOpId();
    try {
      // 1. Applique l'adresse au destinataire
      await db
        .collection("companies").doc(companyId)
        .collection("messages").doc(messageId)
        .collection("recipients").doc(record.key)
        .update({
          return_address_name: values.return_address_name || "",
          return_address_line_1: values.return_address_line_1 || "",
          return_address_line_2: values.return_address_line_2 || "",
          return_address_postal_code: values.return_address_postal_code || "",
          return_address_city: values.return_address_city || "",
          last_updated: firebase.firestore.FieldValue.serverTimestamp(),
          last_change: stamp({ op }),
        });

      // 2. Optionnel : enregistre l'adresse dans le carnet du client
      if (saveToBook) {
        await db.collection("companies").doc(companyId)
          .collection("return_addresses")
          .add({
            label: values.book_label || values.return_address_name || "Adresse",
            name: values.return_address_name || "",
            address_line_1: values.return_address_line_1 || "",
            address_line_2: values.return_address_line_2 || "",
            postal_code: values.return_address_postal_code || "",
            city: values.return_address_city || "",
            is_default: false,
            created_at: firebase.firestore.FieldValue.serverTimestamp(),
            updated_at: firebase.firestore.FieldValue.serverTimestamp(),
            last_change: stamp({ op }),
          });
      }

      message.success("Adresse de retour enregistrée");
      onClose();
    } catch (error) {
      message.error("Erreur lors de l'enregistrement");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title={
        record
          ? `Adresse de retour — ${record.first_name || ""} ${record.last_name || ""}`.trim()
          : "Adresse de retour"
      }
      visible={!!record}
      onOk={handleSave}
      onCancel={onClose}
      okText="Enregistrer"
      cancelText="Annuler"
      confirmLoading={submitting}
      destroyOnClose
    >
      <Form.Item label="Utiliser une adresse enregistrée du client">
        <Select
          placeholder={
            savedAddresses.length > 0
              ? "Sélectionner une adresse du carnet"
              : "Aucune adresse enregistrée pour ce client"
          }
          onChange={applySaved}
          allowClear
          disabled={savedAddresses.length === 0}
          notFoundContent="Aucune adresse enregistrée"
        >
          {savedAddresses.map((a) => (
            <Select.Option key={a.id} value={a.id}>
              {a.label} — {a.name} ({a.postal_code} {a.city}){a.is_default ? " · défaut" : ""}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
      <Divider style={{ margin: "8px 0 16px" }}>
        {savedAddresses.length > 0 ? "ou saisir / modifier" : "saisir l'adresse"}
      </Divider>

      <Form form={form} layout="vertical">
        <Form.Item
          name="return_address_name"
          label="Nom / Raison sociale"
          rules={[{ required: true, message: "Requis" }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="return_address_line_1"
          label="Adresse"
          rules={[{ required: true, message: "Requis" }]}
        >
          <Input />
        </Form.Item>
        <Form.Item name="return_address_line_2" label="Complément d'adresse">
          <Input />
        </Form.Item>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name="return_address_postal_code"
              label="Code postal"
              rules={[
                { required: true, message: "Requis" },
                { pattern: /^\d{5}$/, message: "5 chiffres" },
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={16}>
            <Form.Item
              name="return_address_city"
              label="Ville"
              rules={[{ required: true, message: "Requis" }]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>

        <Checkbox checked={saveToBook} onChange={(e) => setSaveToBook(e.target.checked)}>
          Enregistrer cette adresse dans le carnet du client
        </Checkbox>
        {saveToBook && (
          <Form.Item name="book_label" label="Libellé" style={{ marginTop: 12 }}>
            <Input placeholder="Ex: Siège social" />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
}
